import { getTierLevels, updateTierLevel, updateTierLevelPerks } from '../../api/tierLevels/tierLevelsApi'
import * as apiUpdateFreemiumConfiguration from '../../api/tierLevels/updateFreemiumConfig'
import * as apiAssignLevelReachedPerkToMembersNow from '../../api/tierLevels/assignLevelReachedPerkToMembersNow'
import * as apiFreeUserPoints from '../../api/tierLevels/freeUserPointsApi'

const state = {
    tierLevels: [],
    tierLevel: null,
    loading: false,
    error: null,
    freeUserPointsEnabled: false,
    freeUserPointsPerksConfigurations: []
}

const getters = {
    tierLevels: (state) => state.tierLevels,
    tierLevel: (state) => state.tierLevel,
    loading: (state) => state.loading,
    error: (state) => state.error,

    // Get tier level by ID
    getTierLevelById: (state) => (id) => {
        return state.tierLevels.find(tierLevel => tierLevel.tierLevelId === id)
    },

    // Get sorted tier levels by tier number
    sortedTierLevels: (state) => {
        return [...state.tierLevels].sort((a, b) => a.tierLevelNumber - b.tierLevelNumber)
    },

    // Get all tier levels (no filtering needed since enable/disable is removed)
    allTierLevels: (state) => {
        return state.tierLevels
    },

    // Free user points getters
    freeUserPointsEnabled: (state) => state.freeUserPointsEnabled,
    freeUserPointsPerksConfigurations: (state) => state.freeUserPointsPerksConfigurations
}

const mutations = {
    SET_LOADING(state, loading) {
        state.loading = loading
    },

    SET_ERROR(state, error) {
        state.error = error
    },

    SET_TIER_LEVELS(state, tierLevels) {
        state.tierLevels = tierLevels
    },

    SET_TIER_LEVEL(state, tierLevel) {
        state.tierLevel = tierLevel
    },

    UPDATE_TIER_LEVEL(state, updatedTierLevel) {
        const index = state.tierLevels.findIndex(tl => tl.tierLevelId === updatedTierLevel.tierLevelId)
        if (index !== -1) {
            state.tierLevels.splice(index, 1, updatedTierLevel)
        }
    },

    CLEAR_ERROR(state) {
        state.error = null
    },

    // Free user points mutations
    SET_FREE_USER_POINTS_CONFIG(state, config) {
        state.freeUserPointsEnabled = config.freeUserPointsEnabled || config.FreeUserPointsEnabled
        const configurations = config.freeUserPointsPerksConfigurations || config.FreeUserPointsPerksConfigurations || []
        
        // Ensure all configurations have the new fields with default values
        const normalizedConfigurations = configurations.map(config => ({
            ...config,
            freeDaysToAward: config.freeDaysToAward ?? 0,
            shouldShowPopup: config.shouldShowPopup ?? false,
            numberOfTimesToShowPopup: config.numberOfTimesToShowPopup ?? 1,
            popupTitle: config.popupTitle ?? {
                translations: [
                    { languageCode: "en", text: "" },
                    { languageCode: "da", text: "" },
                    { languageCode: "fi", text: "" },
                    { languageCode: "no", text: "" },
                ]
            },
            popupDescription: config.popupDescription ?? {
                translations: [
                    { languageCode: "en", text: "" },
                    { languageCode: "da", text: "" },
                    { languageCode: "fi", text: "" },
                    { languageCode: "no", text: "" },
                ]
            }
        }))
        
        state.freeUserPointsPerksConfigurations = normalizedConfigurations.sort((a, b) => a.pointsRequired - b.pointsRequired)
    },

    SET_FREE_USER_POINTS_ENABLED(state, enabled) {
        state.freeUserPointsEnabled = enabled
    },

    SET_FREE_USER_POINTS_PERKS_CONFIGURATIONS(state, configurations) {
        // Ensure all configurations have the new fields with default values
        const normalizedConfigurations = configurations.map(config => ({
            ...config,
            freeDaysToAward: config.freeDaysToAward ?? 0,
            shouldShowPopup: config.shouldShowPopup ?? false,
            numberOfTimesToShowPopup: config.numberOfTimesToShowPopup ?? 1,
            popupTitle: config.popupTitle ?? {
                translations: [
                    { languageCode: "en", text: "" },
                    { languageCode: "da", text: "" },
                    { languageCode: "fi", text: "" },
                    { languageCode: "no", text: "" },
                ]
            },
            popupDescription: config.popupDescription ?? {
                translations: [
                    { languageCode: "en", text: "" },
                    { languageCode: "da", text: "" },
                    { languageCode: "fi", text: "" },
                    { languageCode: "no", text: "" },
                ]
            }
        }))
        
        state.freeUserPointsPerksConfigurations = normalizedConfigurations.sort((a, b) => a.pointsRequired - b.pointsRequired)
    }
}

const actions = {
    async getTierLevels({ commit }) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            const response = await getTierLevels()
            commit('SET_TIER_LEVELS', response.data || [])
            return response.data
        } catch (error) {
            console.error('Error fetching tier levels:', error)
            commit('SET_ERROR', error.message || 'Failed to fetch tier levels')
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async updateTierLevel({ commit }, { id, data }) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            await updateTierLevel(id, data)

            // Update the tier level in state
            const updatedTierLevel = {
                tierLevelId: id,
                ...data
            }
            commit('UPDATE_TIER_LEVEL', updatedTierLevel)

            return updatedTierLevel
        } catch (error) {
            console.error('Error updating tier level:', error)
            commit('SET_ERROR', error.message || 'Failed to update tier level')
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async updateTierLevelPerks({ commit }, { id, perkConfigs }) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            // Use the specific perk configs endpoint that only updates perk configurations
            await updateTierLevelPerks(id, perkConfigs)

            // Update the tier level in state
            const existingTierLevel = state.tierLevels.find(tl => tl.tierLevelId === id)
            if (existingTierLevel) {
                const updatedTierLevel = {
                    ...existingTierLevel,
                    perkConfigs: perkConfigs.perkConfigs || [],
                    levelReachedOnetimePerkConfigs: perkConfigs.levelReachedOnetimePerkConfigs || []
                }
                commit('UPDATE_TIER_LEVEL', updatedTierLevel)
            }

            return true
        } catch (error) {
            console.error('Error updating tier level perks:', error)
            commit('SET_ERROR', error.message || 'Failed to update tier level perks')
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async updateFreemiumConfig({ commit }, enabled) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            await apiUpdateFreemiumConfiguration.put(enabled)
        } catch (error) {
            console.error('Error updating freemium configuration:', error)
            commit('SET_ERROR', error.message || 'Failed to update freemium configuration')
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async assignLevelReachedPerkToMembersNow({ commit }, { tierLevelId, perkConfig }) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            const result = await apiAssignLevelReachedPerkToMembersNow.post(tierLevelId, { perkConfig })
            return result
        } catch (error) {
            console.error('Error assigning level reached perk to existing members:', error)
            commit('SET_ERROR', error.message || 'Failed to assign perk to existing members')
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async getFreeUserPointsConfiguration({ commit }) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            const response = await apiFreeUserPoints.getFreeUserPointsConfiguration()
            commit('SET_FREE_USER_POINTS_CONFIG', response)
            return response
        } catch (error) {
            console.error('Error fetching free user points configuration:', error)
            commit('SET_ERROR', error.message || 'Failed to fetch free user points configuration')
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async updateFreeUserPointsEnabled({ commit }, enabled) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            await apiFreeUserPoints.updateFreeUserPointsEnabled(enabled)
            commit('SET_FREE_USER_POINTS_ENABLED', enabled)
            return enabled
        } catch (error) {
            console.error('Error updating free user points enabled:', error)
            commit('SET_ERROR', error.message || 'Failed to update free user points setting')
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    async updateFreeUserPointsPerksConfigurations({ commit }, configurations) {
        commit('SET_LOADING', true)
        commit('CLEAR_ERROR')

        try {
            await apiFreeUserPoints.updateFreeUserPointsPerksConfigurations(configurations)
            commit('SET_FREE_USER_POINTS_PERKS_CONFIGURATIONS', configurations)
            return configurations
        } catch (error) {
            console.error('Error updating free user points perks configurations:', error)
            commit('SET_ERROR', error.message || 'Failed to update free user points perks configurations')
            throw error
        } finally {
            commit('SET_LOADING', false)
        }
    },

    // Clear error state
    clearError({ commit }) {
        commit('CLEAR_ERROR')
    },

    // Reset store state
    resetStore({ commit }) {
        commit('SET_TIER_LEVELS', [])
        commit('SET_TIER_LEVEL', null)
        commit('SET_LOADING', false)
        commit('CLEAR_ERROR')
    }
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions
} 